import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { nasabahService, NasabahProfile } from '@/services/nasabahService';
import { transactionService } from '@/services/transactionService';
import { withdrawalService } from '@/services/withdrawalService';
import { Transaction, WithdrawalRequest } from '@/types';
import { UserRole } from '@/types/enums';

interface NasabahContextType {
  profile: NasabahProfile | null;
  transactions: Transaction[];
  withdrawalRequests: WithdrawalRequest[];
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  refreshWithdrawals: () => Promise<void>;
}

const NasabahContext = createContext<NasabahContextType | undefined>(undefined);

export const useNasabah = () => {
  const context = useContext(NasabahContext);
  if (!context) {
    throw new Error('useNasabah must be used within NasabahProvider');
  }
  return context;
};

interface NasabahProviderProps {
  children: ReactNode;
}

export const NasabahProvider: React.FC<NasabahProviderProps> = ({ children }) => {
  const { user, isAuthenticated, effectiveRole } = useAuth();
  const [profile, setProfile] = useState<NasabahProfile | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when user is authenticated as nasabah — termasuk staf yang
  // sedang di Mode Nasabah (effectiveRole = NASABAH; staf punya profil
  // nasabah otomatis, lihat migrasi 0053).
  useEffect(() => {
    if (isAuthenticated && effectiveRole === UserRole.NASABAH) {
      loadNasabahData();
    } else {
      // Clear data if not nasabah
      setProfile(null);
      setTransactions([]);
      setWithdrawalRequests([]);
    }
  }, [isAuthenticated, user, effectiveRole]);

  const loadNasabahData = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Profil harus dimuat lebih dulu karena transaksi & penarikan
      // bergantung pada profile.id. Setelah itu keduanya bisa paralel.
      const { data: prof, error: profileError } = await nasabahService.getProfileByUserId(user.id);

      if (profileError) {
        setError(profileError);
        return;
      }

      setProfile(prof);
      if (!prof) return;

      const [txResult, wdResult] = await Promise.all([
        transactionService.getTransactions(prof.id),
        withdrawalService.getWithdrawalRequests(prof.id),
      ]);

      if (txResult.error) setError(txResult.error);
      else setTransactions(txResult.data);

      if (wdResult.error) setError(wdResult.error);
      else setWithdrawalRequests(wdResult.data);
    } catch (err) {
      setError('Gagal memuat data nasabah');
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await nasabahService.getProfileByUserId(user.id);
      
      if (error) {
        setError(error);
        return;
      }

      setProfile(data);
    } catch (err) {
      setError('Gagal memuat profil');
    }
  };

  const refreshTransactions = async () => {
    if (!profile) return;

    try {
      const { data, error } = await transactionService.getTransactions(profile.id);
      
      if (error) {
        setError(error);
        return;
      }

      setTransactions(data);
    } catch (err) {
      setError('Gagal memuat transaksi');
    }
  };

  const refreshWithdrawals = async () => {
    if (!profile) return;

    try {
      const { data, error } = await withdrawalService.getWithdrawalRequests(profile.id);
      
      if (error) {
        setError(error);
        return;
      }

      setWithdrawalRequests(data);
    } catch (err) {
      setError('Gagal memuat permintaan penarikan');
    }
  };

  const value: NasabahContextType = {
    profile,
    transactions,
    withdrawalRequests,
    loading,
    error,
    refreshProfile,
    refreshTransactions,
    refreshWithdrawals
  };

  return <NasabahContext.Provider value={value}>{children}</NasabahContext.Provider>;
};
